import { OrderStatus } from '../api/orders';

const PIPELINE: { status: OrderStatus; short: string; hint: string }[] = [
  { status: 'pending', short: 'Commande', hint: 'En attente d’acceptation par le livreur' },
  { status: 'accepted', short: 'Acceptée', hint: 'Le livreur a pris la course' },
  { status: 'picked_up', short: 'Collectée', hint: 'Colis récupéré chez l’expéditeur' },
  { status: 'in_transit', short: 'En route', hint: 'Livraison en cours vers le destinataire' },
  { status: 'delivered', short: 'Livrée', hint: 'Remise effectuée' }
];

type Props = {
  status: OrderStatus;
  className?: string;
};

export const OrderWorkflowStepper = ({ status, className = '' }: Props) => {
  if (status === 'cancelled') {
    return (
      <div
        className={`rounded-lg border border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100/80 px-3 py-2.5 text-xs text-slate-700 ${className}`}
        role="status"
      >
        <span className="font-semibold text-slate-800">Parcours interrompu</span>
        <span className="text-slate-600"> — cette commande a été annulée.</span>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div
        className={`rounded-lg border border-red-200 bg-red-50/90 px-3 py-2.5 text-xs text-red-800 ${className}`}
        role="status"
      >
        <span className="font-semibold">Incident de livraison</span>
        <span> — la course s’est arrêtée avant la livraison (contactez le support si besoin).</span>
      </div>
    );
  }

  const isComplete = status === 'delivered';
  const activeIndex = PIPELINE.findIndex((s) => s.status === status);
  const current = PIPELINE[Math.max(0, activeIndex)];

  return (
    <div className={className}>
      <p className="sr-only">
        Étapes du parcours logistique.
        {isComplete ? ' Livraison terminée.' : ` Étape actuelle : ${current.short}.`}
      </p>
      <ol className="flex w-full items-start gap-0">
        {PIPELINE.map((step, i) => {
          const done = isComplete || i < activeIndex;
          const currentStep = !isComplete && i === activeIndex;
          const isLast = i === PIPELINE.length - 1;

          return (
            <li
              key={step.status}
              className="flex min-w-0 flex-1 flex-col items-center"
              aria-current={currentStep ? 'step' : undefined}
            >
              <div className="flex w-full items-center">
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold transition-colors ${
                    done
                      ? 'border-emerald-500 bg-emerald-500 text-white shadow-sm'
                      : currentStep
                        ? 'border-brand-500 bg-white text-brand-600 shadow-md ring-4 ring-brand-500/15'
                        : 'border-slate-200 bg-white text-slate-400'
                  }`}
                  title={step.hint}
                >
                  {done ? '✓' : i + 1}
                </div>
                {!isLast && (
                  <div
                    className={`mx-0.5 h-0.5 min-w-[8px] flex-1 rounded-full ${done ? 'bg-emerald-400' : 'bg-slate-200'}`}
                    aria-hidden
                  />
                )}
              </div>
              <span
                className={`mt-1.5 hidden text-center text-[10px] font-medium leading-tight sm:block ${
                  currentStep ? 'text-brand-700' : done ? 'text-emerald-800' : 'text-slate-400'
                }`}
              >
                {step.short}
              </span>
            </li>
          );
        })}
      </ol>
      <p className="mt-2 text-center text-[11px] text-slate-500 sm:text-left">
        {isComplete ? (
          <>
            <span className="font-medium text-emerald-800">Livraison terminée</span>
            <span> — le colis a été remis au destinataire.</span>
          </>
        ) : (
          <>
            <span className="font-medium text-slate-700">Étape en cours :</span> {current.hint}
          </>
        )}
      </p>
    </div>
  );
};
